import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { ArrowLeft, UserMinus, UserPlus, Pause, Play, X } from 'lucide-react';
import { format } from 'date-fns';

export const CircleDetail = () => {
  const { id } = useParams();
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, [id]);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/circles/${id}`);
      setCircle(res.data.data?.circle || res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this member from the circle?')) return;
    try {
      await api.delete(`/admin/circles/${id}/members/${memberId}`);
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const replaceMember = async (memberId) => {
    const newUserId = window.prompt('Enter the User ID of the replacement member:');
    if (!newUserId) return;
    try {
      await api.post(`/admin/circles/${id}/members/${memberId}/replace`, { newUserId });
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  const setStatus = async (status) => {
    const verbs = { PAUSED: 'freeze', CANCELLED: 'cancel', ACTIVE: 'reactivate' };
    let reason = null;
    if (status !== 'ACTIVE') {
      reason = window.prompt(`Reason to ${verbs[status]} this circle (optional):`);
      if (reason === null) return;
    } else {
      if (!window.confirm('Reactivate this circle?')) return;
    }
    try {
      await api.put(`/admin/circles/${id}/status`, { status, reason });
      fetch();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error?.message || e.message)); }
  };

  if (loading) return <div className="loader-container"><div className="spinner"></div></div>;
  if (!circle) return <div>Circle not found</div>;

  const members = circle.members || [];

  return (
    <div>
      <Link to="/circles" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', textDecoration: 'none', marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back to circles
      </Link>

      <div className="card-header" style={{ padding: '0 0 24px 0', border: 'none' }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>{circle.name}</h1>
          <div style={{ color: 'var(--text-secondary)' }}>{circle.type} · {members.length} / {circle.totalMembers} members · ${Number(circle.monthlyContribution).toLocaleString()}/mo</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {circle.status === 'ACTIVE' && (
            <button className="btn-secondary" onClick={() => setStatus('PAUSED')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Pause size={14} /> Freeze
            </button>
          )}
          {circle.status === 'PAUSED' && (
            <button className="btn-success" onClick={() => setStatus('ACTIVE')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Play size={14} /> Resume
            </button>
          )}
          {circle.status !== 'CANCELLED' && circle.status !== 'COMPLETED' && (
            <button className="btn-danger" onClick={() => setStatus('CANCELLED')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <X size={14} /> Cancel
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="glass" style={{ padding: 20 }}><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>STATUS</div><div style={{ fontSize: 18, fontWeight: 600 }}><span className={`badge ${circle.status?.toLowerCase()}`}>{circle.status}</span></div></div>
        <div className="glass" style={{ padding: 20 }}><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>POOL BALANCE</div><div style={{ fontSize: 24, fontWeight: 700 }}>${Number(circle.poolBalance || 0).toLocaleString()}</div></div>
        <div className="glass" style={{ padding: 20 }}><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>CURRENT ROUND</div><div style={{ fontSize: 24, fontWeight: 700 }}>{circle.currentRound} / {circle.totalRounds}</div></div>
        <div className="glass" style={{ padding: 20 }}><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>NEXT CHARGE</div><div style={{ fontSize: 16, fontWeight: 600 }}>{circle.nextChargeDate ? format(new Date(circle.nextChargeDate), 'MMM dd, yyyy') : '—'}</div></div>
      </div>

      {/* Members */}
      <div className="glass">
        <div className="card-header"><h3 style={{ margin: 0 }}>Members</h3></div>
        <div className="table-container" style={{ border: 'none', borderRadius: '0 0 16px 16px' }}>
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Position</th>
                <th>Status</th>
                <th>Contributions</th>
                <th>Missed</th>
                <th>Payout</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{m.user?.firstName} {m.user?.lastName}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.user?.email}</div>
                  </td>
                  <td>#{m.payoutPosition}</td>
                  <td><span className={`badge ${(m.status || 'pending').toLowerCase()}`}>{m.status}</span></td>
                  <td>${Number(m.totalContributed || 0).toLocaleString()}</td>
                  <td>{m.missedPayments > 0 ? <span className="badge rejected">{m.missedPayments}</span> : '0'}</td>
                  <td>{m.hasReceivedPayout ? <span className="badge approved">RECEIVED</span> : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn-secondary" title="Replace member"
                              onClick={() => replaceMember(m.id)} style={{ padding: '6px 10px' }}>
                        <UserPlus size={14} />
                      </button>
                      <button className="btn-danger" title="Remove member"
                              onClick={() => removeMember(m.id)} style={{ padding: '6px 10px' }}>
                        <UserMinus size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>No members</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
